import { FC, useEffect, useState } from 'react'
import { Modal, Form, Input, Rate, Button, Tooltip } from 'antd'
import { toast } from 'react-toastify'
import { useSubmitFeedbackMutation } from 'api/users-api'
import { useWorkspace } from 'hooks/useWorkspace'
import { useParams } from 'react-router-dom'
import { Pto } from '@outcoreaix/pto'
import { localStorageService } from 'app/local-storage'
import useModal from 'hooks/useModal'
import { useSelector } from 'react-redux'
import { StarOutlined } from '@ant-design/icons'
import type { RootState } from 'app/store'

type FormProps = {
  starsAmount: number
  description: string
}
type Props = {
  isNavMenuLink?: boolean
  onOpen?: () => void
  catchEmitter?: boolean
}

const Feedback: FC<Props> = ({ isNavMenuLink, onOpen, catchEmitter = false }) => {
  const params = useParams()
  const { currentWorkspace, currentWorkspaceId } = useWorkspace()

  const [submitFeedback] = useSubmitFeedbackMutation()

  const [form] = Form.useForm<FormProps>()

  const { openModal, isVisible, closeModal } = useModal()
  const [fieldsWereTouched, setFieldsWereTouched] = useState(false)
  const [isStarsSelected, setIsStarsSelected] = useState(false)
  const currentSpaceId = useSelector((state: RootState) => state.chat.currentSpaceId)

  useEffect(() => {
    if (!catchEmitter) return

    const handler = (value: boolean) => {
      if (value && !isVisible) {
        handleOpenModal()
        localStorageService.setPendingFeedback(false)
      }
    }

    localStorageService.onFeedbackChange(handler)
    return () => {
      localStorageService.offFeedbackChange(handler)
    }
  }, [catchEmitter])

  const onStarsChange = () => {
    setIsStarsSelected(true)
  }

  const handleOpenModal = async () => {
    if (isVisible) return
    openModal()
    onOpen?.()
  }

  const onSubmit = async (values: FormProps) => {
    await submitFeedback({
      starsAmount: values.starsAmount,
      description: values.description || '',
      workspaceId: currentWorkspaceId as number,
      spaceId:
        (params.spaceId || currentSpaceId) &&
        currentWorkspace?.currentMember?.role === Pto.Workspaces.WorkspaceRole.Contact
          ? (params.spaceId ? Number(params.spaceId) : currentSpaceId) || null
          : null
    })
      .unwrap()
      .then(async () => {
        await onCancel()
        localStorageService.setPendingFeedback(false)
      })
      .catch(() => {
        toast.error('Failed to send feedback')
      })
  }

  const onCancel = async () => {
    closeModal()
    form.resetFields()
    setIsStarsSelected(false)
    setFieldsWereTouched(false)
  }

  return (
    <>
      {isNavMenuLink ? (
        <Button
          type="text"
          icon={<StarOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px]" />}
          className="w-full flex h-10 justify-normal mr-auto hover:!bg-secondaryWithOpacity font-medium"
          onClick={handleOpenModal}
        >
          Rate your experience
        </Button>
      ) : (
        <Tooltip title="Leave feedback">
          <Button size="middle" icon={<StarOutlined />} onClick={handleOpenModal} />
        </Tooltip>
      )}
      <Modal
        open={isVisible}
        onCancel={onCancel}
        cancelText="Skip"
        okButtonProps={{ disabled: !isStarsSelected || !fieldsWereTouched }}
        okText={isStarsSelected ? 'Submit' : 'Save'}
        destroyOnClose
        onOk={() => form.submit()}
        className="feedback-modal max-w-96 text-base"
      >
        <h2 className="text-center m-3 mb-5">We would love your feedback!</h2>
        <p className="text-center">
          We hope that you are enjoying using
          <br />
          Alex AI. How would you rate your experience today?
        </p>
        <Form
          layout="vertical"
          onFinish={onSubmit}
          form={form}
          onValuesChange={() => setFieldsWereTouched(true)}
          className="mb-7"
        >
          <Form.Item
            className="rate-container"
            name="starsAmount"
            rules={[{ required: true, message: 'Please rate the app' }]}
          >
            <Rate onChange={onStarsChange} allowClear />
          </Form.Item>
          {isStarsSelected && (
            <Form.Item label="What made you choose this score?" name="description">
              <Input.TextArea autoSize />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  )
}

export default Feedback
